// ═══════════════════════════════════════════
// Color themes for Monaco editor and xterm terminal
// ═══════════════════════════════════════════

export interface EditorTheme {
  id: string;
  name: string;
  mode: 'dark' | 'light';
  monaco: {
    base: 'vs-dark' | 'vs' | 'hc-black';
    inherit: boolean;
    rules: Array<{ token: string; foreground: string; fontStyle?: string }>;
    colors: Record<string, string>;
  };
}

export interface TerminalTheme {
  id: string;
  name: string;
  mode: 'dark' | 'light';
  colors: {
    background: string;
    foreground: string;
    cursor: string;
    cursorAccent: string;
    selectionBackground: string;
    selectionForeground?: string;
    black: string;
    red: string;
    green: string;
    yellow: string;
    blue: string;
    magenta: string;
    cyan: string;
    white: string;
    brightBlack: string;
    brightRed: string;
    brightGreen: string;
    brightYellow: string;
    brightBlue: string;
    brightMagenta: string;
    brightCyan: string;
    brightWhite: string;
  };
}

// ═══════════════════════════════════════════
// Grid Themes (AG Grid)
// ═══════════════════════════════════════════

export interface GridTheme {
  id: string;
  name: string;
  mode: 'dark' | 'light';
  colors: {
    background: string;
    headerBackground: string;
    oddRowBackground: string;
    rowHover: string;
    selectedRow: string;
    border: string;
    headerForeground: string;
    foreground: string;
  };
}

export const GRID_THEMES: GridTheme[] = [
  // ── Dark ──
  { id: 'dbgo-dark', name: 'DBGo Dark', mode: 'dark', colors: {
    background: '#1a1d25', headerBackground: '#1e2129', oddRowBackground: '#171920',
    rowHover: '#1f2230', selectedRow: '#1c2a4a', border: '#1e2028',
    headerForeground: '#8b8fa3', foreground: '#e1e4eb',
  }},
  { id: 'monokai', name: 'Monokai', mode: 'dark', colors: {
    background: '#272822', headerBackground: '#2d2e27', oddRowBackground: '#2b2c25',
    rowHover: '#3e3d32', selectedRow: '#49483e', border: '#3b3a32',
    headerForeground: '#a6a28c', foreground: '#f8f8f2',
  }},
  { id: 'one-dark-pro', name: 'One Dark Pro', mode: 'dark', colors: {
    background: '#282c34', headerBackground: '#2c313a', oddRowBackground: '#2b3039',
    rowHover: '#333842', selectedRow: '#2c3e50', border: '#3b4048',
    headerForeground: '#848da0', foreground: '#abb2bf',
  }},
  { id: 'dracula', name: 'Dracula', mode: 'dark', colors: {
    background: '#282a36', headerBackground: '#2d2f3d', oddRowBackground: '#2a2c39',
    rowHover: '#343746', selectedRow: '#44475a', border: '#3a3c4e',
    headerForeground: '#8892b0', foreground: '#f8f8f2',
  }},
  { id: 'solarized-dark', name: 'Solarized Dark', mode: 'dark', colors: {
    background: '#002b36', headerBackground: '#003541', oddRowBackground: '#00303b',
    rowHover: '#004050', selectedRow: '#073642', border: '#094552',
    headerForeground: '#657b83', foreground: '#839496',
  }},
  { id: 'nord', name: 'Nord', mode: 'dark', colors: {
    background: '#2e3440', headerBackground: '#333a47', oddRowBackground: '#313844',
    rowHover: '#3b4252', selectedRow: '#434c5e', border: '#3b4252',
    headerForeground: '#7b88a1', foreground: '#d8dee9',
  }},
  { id: 'github-dark', name: 'GitHub Dark', mode: 'dark', colors: {
    background: '#0d1117', headerBackground: '#161b22', oddRowBackground: '#111820',
    rowHover: '#1c2128', selectedRow: '#1f2937', border: '#21262d',
    headerForeground: '#8b949e', foreground: '#c9d1d9',
  }},
  { id: 'catppuccin-mocha', name: 'Catppuccin Mocha', mode: 'dark', colors: {
    background: '#1e1e2e', headerBackground: '#242436', oddRowBackground: '#21212f',
    rowHover: '#2a2a3c', selectedRow: '#313244', border: '#2a2a3c',
    headerForeground: '#7f849c', foreground: '#cdd6f4',
  }},
  { id: 'gruvbox-dark', name: 'Gruvbox Dark', mode: 'dark', colors: {
    background: '#282828', headerBackground: '#2e2e2e', oddRowBackground: '#2b2b2b',
    rowHover: '#3c3836', selectedRow: '#504945', border: '#3c3836',
    headerForeground: '#a89984', foreground: '#ebdbb2',
  }},
  { id: 'tokyo-night', name: 'Tokyo Night', mode: 'dark', colors: {
    background: '#1a1b26', headerBackground: '#1f2030', oddRowBackground: '#1c1d2b',
    rowHover: '#24253a', selectedRow: '#2a2b40', border: '#24253a',
    headerForeground: '#737aa2', foreground: '#a9b1d6',
  }},
  // ── Light ──
  { id: 'dbgo-light', name: 'DBGo Light', mode: 'light', colors: {
    background: '#ffffff', headerBackground: '#f0f0f2', oddRowBackground: '#f8f8fa',
    rowHover: '#eaeaed', selectedRow: '#dce6f5', border: '#e0e0e5',
    headerForeground: '#6b6b80', foreground: '#1a1a2e',
  }},
  { id: 'github-light', name: 'GitHub Light', mode: 'light', colors: {
    background: '#ffffff', headerBackground: '#f6f8fa', oddRowBackground: '#f9fafb',
    rowHover: '#f0f2f4', selectedRow: '#ddf4ff', border: '#d0d7de',
    headerForeground: '#57606a', foreground: '#24292f',
  }},
  { id: 'solarized-light', name: 'Solarized Light', mode: 'light', colors: {
    background: '#fdf6e3', headerBackground: '#f5eed6', oddRowBackground: '#f9f3de',
    rowHover: '#eee8d5', selectedRow: '#e0dcc7', border: '#e0dcc7',
    headerForeground: '#657b83', foreground: '#586e75',
  }},
  { id: 'one-light', name: 'One Light', mode: 'light', colors: {
    background: '#fafafa', headerBackground: '#f0f0f0', oddRowBackground: '#f5f5f5',
    rowHover: '#e8e8e8', selectedRow: '#d7e4f2', border: '#e0e0e0',
    headerForeground: '#696c77', foreground: '#383a42',
  }},
  { id: 'catppuccin-latte', name: 'Catppuccin Latte', mode: 'light', colors: {
    background: '#eff1f5', headerBackground: '#e6e9ef', oddRowBackground: '#eaecf1',
    rowHover: '#dce0e8', selectedRow: '#ccd0da', border: '#ccd0da',
    headerForeground: '#7c7f93', foreground: '#4c4f69',
  }},
];

export const DARK_GRID_THEMES = GRID_THEMES.filter(t => t.mode === 'dark');
export const LIGHT_GRID_THEMES = GRID_THEMES.filter(t => t.mode === 'light');

// ═══════════════════════════════════════════
// Editor Themes (Monaco)
// ═══════════════════════════════════════════

export const EDITOR_THEMES: EditorTheme[] = [
  // 1. DBGo Dark (default) — the original custom theme
  {
    id: 'dbgo-dark',
    name: 'DBGo Dark',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'c678dd', fontStyle: 'bold' },
        { token: 'string', foreground: '98c379' },
        { token: 'number', foreground: 'd19a66' },
        { token: 'comment', foreground: '5c6370', fontStyle: 'italic' },
        { token: 'type', foreground: 'e5c07b' },
        { token: 'predefined', foreground: '61afef' },
        { token: 'operator', foreground: '56b6c2' },
      ],
      colors: {
        'editor.background': '#12141a',
        'editor.foreground': '#e1e4eb',
        'editor.selectionBackground': '#4a7fff4d',
        'editor.lineHighlightBackground': '#1e202833',
        'editorCursor.foreground': '#4a7fff',
        'editorLineNumber.foreground': '#5c6070',
        'editorLineNumber.activeForeground': '#e1e4eb',
        'editorGutter.background': '#13151a',
      },
    },
  },

  // 2. Monokai — classic Sublime Text theme
  {
    id: 'monokai',
    name: 'Monokai',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'f92672', fontStyle: 'bold' },
        { token: 'string', foreground: 'e6db74' },
        { token: 'number', foreground: 'ae81ff' },
        { token: 'comment', foreground: '75715e', fontStyle: 'italic' },
        { token: 'type', foreground: '66d9ef', fontStyle: 'italic' },
        { token: 'predefined', foreground: 'a6e22e' },
        { token: 'operator', foreground: 'f92672' },
      ],
      colors: {
        'editor.background': '#272822',
        'editor.foreground': '#f8f8f2',
        'editor.selectionBackground': '#49483e',
        'editor.lineHighlightBackground': '#3e3d32',
        'editorCursor.foreground': '#f8f8f0',
        'editorLineNumber.foreground': '#90908a',
        'editorLineNumber.activeForeground': '#c2c2bf',
        'editorGutter.background': '#272822',
      },
    },
  },

  // 3. One Dark Pro — Atom's One Dark
  {
    id: 'one-dark-pro',
    name: 'One Dark Pro',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'c678dd', fontStyle: 'bold' },
        { token: 'string', foreground: '98c379' },
        { token: 'number', foreground: 'd19a66' },
        { token: 'comment', foreground: '5c6370', fontStyle: 'italic' },
        { token: 'type', foreground: 'e5c07b' },
        { token: 'predefined', foreground: '61afef' },
        { token: 'operator', foreground: '56b6c2' },
      ],
      colors: {
        'editor.background': '#282c34',
        'editor.foreground': '#abb2bf',
        'editor.selectionBackground': '#3e4451',
        'editor.lineHighlightBackground': '#2c313c',
        'editorCursor.foreground': '#528bff',
        'editorLineNumber.foreground': '#495162',
        'editorLineNumber.activeForeground': '#abb2bf',
        'editorGutter.background': '#282c34',
      },
    },
  },

  // 4. Dracula — popular dark theme
  {
    id: 'dracula',
    name: 'Dracula',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'ff79c6', fontStyle: 'bold' },
        { token: 'string', foreground: 'f1fa8c' },
        { token: 'number', foreground: 'bd93f9' },
        { token: 'comment', foreground: '6272a4', fontStyle: 'italic' },
        { token: 'type', foreground: '8be9fd', fontStyle: 'italic' },
        { token: 'predefined', foreground: '50fa7b' },
        { token: 'operator', foreground: 'ff79c6' },
      ],
      colors: {
        'editor.background': '#282a36',
        'editor.foreground': '#f8f8f2',
        'editor.selectionBackground': '#44475a',
        'editor.lineHighlightBackground': '#44475a75',
        'editorCursor.foreground': '#f8f8f2',
        'editorLineNumber.foreground': '#6272a4',
        'editorLineNumber.activeForeground': '#f8f8f2',
        'editorGutter.background': '#282a36',
      },
    },
  },

  // 5. Solarized Dark — Ethan Schoonover's classic
  {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '859900', fontStyle: 'bold' },
        { token: 'string', foreground: '2aa198' },
        { token: 'number', foreground: 'd33682' },
        { token: 'comment', foreground: '586e75', fontStyle: 'italic' },
        { token: 'type', foreground: 'b58900' },
        { token: 'predefined', foreground: '268bd2' },
        { token: 'operator', foreground: '93a1a1' },
      ],
      colors: {
        'editor.background': '#002b36',
        'editor.foreground': '#839496',
        'editor.selectionBackground': '#073642',
        'editor.lineHighlightBackground': '#073642',
        'editorCursor.foreground': '#839496',
        'editorLineNumber.foreground': '#586e75',
        'editorLineNumber.activeForeground': '#93a1a1',
        'editorGutter.background': '#002b36',
      },
    },
  },

  // 6. Nord — Arctic-inspired
  {
    id: 'nord',
    name: 'Nord',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '81a1c1', fontStyle: 'bold' },
        { token: 'string', foreground: 'a3be8c' },
        { token: 'number', foreground: 'b48ead' },
        { token: 'comment', foreground: '616e88', fontStyle: 'italic' },
        { token: 'type', foreground: '8fbcbb' },
        { token: 'predefined', foreground: '88c0d0' },
        { token: 'operator', foreground: '81a1c1' },
      ],
      colors: {
        'editor.background': '#2e3440',
        'editor.foreground': '#d8dee9',
        'editor.selectionBackground': '#434c5ecc',
        'editor.lineHighlightBackground': '#3b4252',
        'editorCursor.foreground': '#d8dee9',
        'editorLineNumber.foreground': '#4c566a',
        'editorLineNumber.activeForeground': '#d8dee9',
        'editorGutter.background': '#2e3440',
      },
    },
  },

  // 7. GitHub Dark — GitHub's dark theme
  {
    id: 'github-dark',
    name: 'GitHub Dark',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'ff7b72', fontStyle: 'bold' },
        { token: 'string', foreground: 'a5d6ff' },
        { token: 'number', foreground: '79c0ff' },
        { token: 'comment', foreground: '8b949e', fontStyle: 'italic' },
        { token: 'type', foreground: 'ffa657' },
        { token: 'predefined', foreground: 'd2a8ff' },
        { token: 'operator', foreground: 'ff7b72' },
      ],
      colors: {
        'editor.background': '#0d1117',
        'editor.foreground': '#c9d1d9',
        'editor.selectionBackground': '#264f78',
        'editor.lineHighlightBackground': '#161b22',
        'editorCursor.foreground': '#c9d1d9',
        'editorLineNumber.foreground': '#484f58',
        'editorLineNumber.activeForeground': '#c9d1d9',
        'editorGutter.background': '#0d1117',
      },
    },
  },

  // 8. Catppuccin Mocha — pastel dark theme
  {
    id: 'catppuccin-mocha',
    name: 'Catppuccin Mocha',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'cba6f7', fontStyle: 'bold' },
        { token: 'string', foreground: 'a6e3a1' },
        { token: 'number', foreground: 'fab387' },
        { token: 'comment', foreground: '6c7086', fontStyle: 'italic' },
        { token: 'type', foreground: 'f9e2af' },
        { token: 'predefined', foreground: '89b4fa' },
        { token: 'operator', foreground: '89dceb' },
      ],
      colors: {
        'editor.background': '#1e1e2e',
        'editor.foreground': '#cdd6f4',
        'editor.selectionBackground': '#585b7066',
        'editor.lineHighlightBackground': '#313244',
        'editorCursor.foreground': '#f5e0dc',
        'editorLineNumber.foreground': '#6c7086',
        'editorLineNumber.activeForeground': '#cdd6f4',
        'editorGutter.background': '#1e1e2e',
      },
    },
  },

  // 9. Gruvbox Dark — retro warm theme
  {
    id: 'gruvbox-dark',
    name: 'Gruvbox Dark',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'fb4934', fontStyle: 'bold' },
        { token: 'string', foreground: 'b8bb26' },
        { token: 'number', foreground: 'd3869b' },
        { token: 'comment', foreground: '928374', fontStyle: 'italic' },
        { token: 'type', foreground: 'fabd2f' },
        { token: 'predefined', foreground: '83a598' },
        { token: 'operator', foreground: 'fe8019' },
      ],
      colors: {
        'editor.background': '#282828',
        'editor.foreground': '#ebdbb2',
        'editor.selectionBackground': '#504945',
        'editor.lineHighlightBackground': '#3c3836',
        'editorCursor.foreground': '#ebdbb2',
        'editorLineNumber.foreground': '#665c54',
        'editorLineNumber.activeForeground': '#ebdbb2',
        'editorGutter.background': '#282828',
      },
    },
  },

  // 10. Tokyo Night — VS Code popular theme
  {
    id: 'tokyo-night',
    name: 'Tokyo Night',
    mode: 'dark',
    monaco: {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '9d7cd8', fontStyle: 'bold' },
        { token: 'string', foreground: '9ece6a' },
        { token: 'number', foreground: 'ff9e64' },
        { token: 'comment', foreground: '565f89', fontStyle: 'italic' },
        { token: 'type', foreground: '2ac3de' },
        { token: 'predefined', foreground: '7aa2f7' },
        { token: 'operator', foreground: '89ddff' },
      ],
      colors: {
        'editor.background': '#1a1b26',
        'editor.foreground': '#a9b1d6',
        'editor.selectionBackground': '#33467c',
        'editor.lineHighlightBackground': '#1e202e',
        'editorCursor.foreground': '#c0caf5',
        'editorLineNumber.foreground': '#3b4261',
        'editorLineNumber.activeForeground': '#a9b1d6',
        'editorGutter.background': '#1a1b26',
      },
    },
  },

  // ═══════════════════════════════════════════
  // Light Editor Themes
  // ═══════════════════════════════════════════

  // 11. DBGo Light — matches the app's light mode palette
  {
    id: 'dbgo-light',
    name: 'DBGo Light',
    mode: 'light',
    monaco: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '7c3aed', fontStyle: 'bold' },
        { token: 'string', foreground: '16a34a' },
        { token: 'number', foreground: 'd97706' },
        { token: 'comment', foreground: '9ca3af', fontStyle: 'italic' },
        { token: 'type', foreground: 'b45309' },
        { token: 'predefined', foreground: '2563eb' },
        { token: 'operator', foreground: '0891b2' },
      ],
      colors: {
        'editor.background': '#ffffff',
        'editor.foreground': '#1a1a2e',
        'editor.selectionBackground': '#2563eb33',
        'editor.lineHighlightBackground': '#f1f5f9',
        'editorCursor.foreground': '#2563eb',
        'editorLineNumber.foreground': '#9ca3af',
        'editorLineNumber.activeForeground': '#1a1a2e',
        'editorGutter.background': '#ffffff',
      },
    },
  },

  // 12. GitHub Light — GitHub's light theme
  {
    id: 'github-light',
    name: 'GitHub Light',
    mode: 'light',
    monaco: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'cf222e', fontStyle: 'bold' },
        { token: 'string', foreground: '0a3069' },
        { token: 'number', foreground: '0550ae' },
        { token: 'comment', foreground: '6e7781', fontStyle: 'italic' },
        { token: 'type', foreground: '953800' },
        { token: 'predefined', foreground: '8250df' },
        { token: 'operator', foreground: 'cf222e' },
      ],
      colors: {
        'editor.background': '#ffffff',
        'editor.foreground': '#24292e',
        'editor.selectionBackground': '#0366d625',
        'editor.lineHighlightBackground': '#f6f8fa',
        'editorCursor.foreground': '#044289',
        'editorLineNumber.foreground': '#8c959f',
        'editorLineNumber.activeForeground': '#24292e',
        'editorGutter.background': '#ffffff',
      },
    },
  },

  // 13. Solarized Light — Ethan Schoonover's light variant
  {
    id: 'solarized-light',
    name: 'Solarized Light',
    mode: 'light',
    monaco: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '859900', fontStyle: 'bold' },
        { token: 'string', foreground: '2aa198' },
        { token: 'number', foreground: 'd33682' },
        { token: 'comment', foreground: '93a1a1', fontStyle: 'italic' },
        { token: 'type', foreground: 'b58900' },
        { token: 'predefined', foreground: '268bd2' },
        { token: 'operator', foreground: '586e75' },
      ],
      colors: {
        'editor.background': '#fdf6e3',
        'editor.foreground': '#657b83',
        'editor.selectionBackground': '#eee8d5',
        'editor.lineHighlightBackground': '#eee8d5',
        'editorCursor.foreground': '#586e75',
        'editorLineNumber.foreground': '#93a1a1',
        'editorLineNumber.activeForeground': '#586e75',
        'editorGutter.background': '#fdf6e3',
      },
    },
  },

  // 14. One Light — Atom's light theme
  {
    id: 'one-light',
    name: 'One Light',
    mode: 'light',
    monaco: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: 'a626a4', fontStyle: 'bold' },
        { token: 'string', foreground: '50a14f' },
        { token: 'number', foreground: '986801' },
        { token: 'comment', foreground: 'a0a1a7', fontStyle: 'italic' },
        { token: 'type', foreground: 'c18401' },
        { token: 'predefined', foreground: '4078f2' },
        { token: 'operator', foreground: '0184bc' },
      ],
      colors: {
        'editor.background': '#fafafa',
        'editor.foreground': '#383a42',
        'editor.selectionBackground': '#e5e5e6',
        'editor.lineHighlightBackground': '#f2f2f2',
        'editorCursor.foreground': '#526fff',
        'editorLineNumber.foreground': '#9d9d9f',
        'editorLineNumber.activeForeground': '#383a42',
        'editorGutter.background': '#fafafa',
      },
    },
  },

  // 15. Catppuccin Latte — pastel light theme
  {
    id: 'catppuccin-latte',
    name: 'Catppuccin Latte',
    mode: 'light',
    monaco: {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '8839ef', fontStyle: 'bold' },
        { token: 'string', foreground: '40a02b' },
        { token: 'number', foreground: 'fe640b' },
        { token: 'comment', foreground: '9ca0b0', fontStyle: 'italic' },
        { token: 'type', foreground: 'df8e1d' },
        { token: 'predefined', foreground: '1e66f5' },
        { token: 'operator', foreground: '04a5e5' },
      ],
      colors: {
        'editor.background': '#eff1f5',
        'editor.foreground': '#4c4f69',
        'editor.selectionBackground': '#acb0be66',
        'editor.lineHighlightBackground': '#e6e9ef',
        'editorCursor.foreground': '#dc8a78',
        'editorLineNumber.foreground': '#9ca0b0',
        'editorLineNumber.activeForeground': '#4c4f69',
        'editorGutter.background': '#eff1f5',
      },
    },
  },
];

// ═══════════════════════════════════════════
// Terminal Themes (xterm)
// ═══════════════════════════════════════════

export const TERMINAL_THEMES: TerminalTheme[] = [
  // 1. DBGo Dark (default)
  {
    id: 'dbgo-dark',
    name: 'DBGo Dark',
    mode: 'dark',
    colors: {
      background: '#0e1015',
      foreground: '#e1e4eb',
      cursor: '#4a7fff',
      cursorAccent: '#0e1015',
      selectionBackground: 'rgba(74, 127, 255, 0.3)',
      selectionForeground: '#ffffff',
      black: '#1a1d25',
      red: '#ef5350',
      green: '#3dd68c',
      yellow: '#f0c556',
      blue: '#4a7fff',
      magenta: '#b07fff',
      cyan: '#4dd0c8',
      white: '#e1e4eb',
      brightBlack: '#5c6070',
      brightRed: '#ef5350',
      brightGreen: '#3dd68c',
      brightYellow: '#f0c556',
      brightBlue: '#7ba4ff',
      brightMagenta: '#c678dd',
      brightCyan: '#56b6c2',
      brightWhite: '#ffffff',
    },
  },

  // 2. Monokai
  {
    id: 'monokai',
    name: 'Monokai',
    mode: 'dark',
    colors: {
      background: '#272822',
      foreground: '#f8f8f2',
      cursor: '#f8f8f0',
      cursorAccent: '#272822',
      selectionBackground: 'rgba(73, 72, 62, 0.6)',
      selectionForeground: '#f8f8f2',
      black: '#272822',
      red: '#f92672',
      green: '#a6e22e',
      yellow: '#f4bf75',
      blue: '#66d9ef',
      magenta: '#ae81ff',
      cyan: '#a1efe4',
      white: '#f8f8f2',
      brightBlack: '#75715e',
      brightRed: '#f92672',
      brightGreen: '#a6e22e',
      brightYellow: '#f4bf75',
      brightBlue: '#66d9ef',
      brightMagenta: '#ae81ff',
      brightCyan: '#a1efe4',
      brightWhite: '#f9f8f5',
    },
  },

  // 3. One Dark Pro
  {
    id: 'one-dark-pro',
    name: 'One Dark Pro',
    mode: 'dark',
    colors: {
      background: '#282c34',
      foreground: '#abb2bf',
      cursor: '#528bff',
      cursorAccent: '#282c34',
      selectionBackground: 'rgba(62, 68, 81, 0.6)',
      selectionForeground: '#abb2bf',
      black: '#3f4451',
      red: '#e06c75',
      green: '#98c379',
      yellow: '#e5c07b',
      blue: '#61afef',
      magenta: '#c678dd',
      cyan: '#56b6c2',
      white: '#abb2bf',
      brightBlack: '#4f5666',
      brightRed: '#be5046',
      brightGreen: '#98c379',
      brightYellow: '#d19a66',
      brightBlue: '#61afef',
      brightMagenta: '#c678dd',
      brightCyan: '#56b6c2',
      brightWhite: '#e6e6e6',
    },
  },

  // 4. Dracula
  {
    id: 'dracula',
    name: 'Dracula',
    mode: 'dark',
    colors: {
      background: '#282a36',
      foreground: '#f8f8f2',
      cursor: '#f8f8f2',
      cursorAccent: '#282a36',
      selectionBackground: 'rgba(68, 71, 90, 0.6)',
      selectionForeground: '#f8f8f2',
      black: '#21222c',
      red: '#ff5555',
      green: '#50fa7b',
      yellow: '#f1fa8c',
      blue: '#bd93f9',
      magenta: '#ff79c6',
      cyan: '#8be9fd',
      white: '#f8f8f2',
      brightBlack: '#6272a4',
      brightRed: '#ff6e6e',
      brightGreen: '#69ff94',
      brightYellow: '#ffffa5',
      brightBlue: '#d6acff',
      brightMagenta: '#ff92df',
      brightCyan: '#a4ffff',
      brightWhite: '#ffffff',
    },
  },

  // 5. Solarized Dark
  {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    mode: 'dark',
    colors: {
      background: '#002b36',
      foreground: '#839496',
      cursor: '#839496',
      cursorAccent: '#002b36',
      selectionBackground: 'rgba(7, 54, 66, 0.8)',
      selectionForeground: '#93a1a1',
      black: '#073642',
      red: '#dc322f',
      green: '#859900',
      yellow: '#b58900',
      blue: '#268bd2',
      magenta: '#d33682',
      cyan: '#2aa198',
      white: '#eee8d5',
      brightBlack: '#586e75',
      brightRed: '#cb4b16',
      brightGreen: '#586e75',
      brightYellow: '#657b83',
      brightBlue: '#839496',
      brightMagenta: '#6c71c4',
      brightCyan: '#93a1a1',
      brightWhite: '#fdf6e3',
    },
  },

  // 6. Nord
  {
    id: 'nord',
    name: 'Nord',
    mode: 'dark',
    colors: {
      background: '#2e3440',
      foreground: '#d8dee9',
      cursor: '#d8dee9',
      cursorAccent: '#2e3440',
      selectionBackground: 'rgba(67, 76, 94, 0.6)',
      selectionForeground: '#d8dee9',
      black: '#3b4252',
      red: '#bf616a',
      green: '#a3be8c',
      yellow: '#ebcb8b',
      blue: '#81a1c1',
      magenta: '#b48ead',
      cyan: '#88c0d0',
      white: '#e5e9f0',
      brightBlack: '#4c566a',
      brightRed: '#bf616a',
      brightGreen: '#a3be8c',
      brightYellow: '#ebcb8b',
      brightBlue: '#81a1c1',
      brightMagenta: '#b48ead',
      brightCyan: '#8fbcbb',
      brightWhite: '#eceff4',
    },
  },

  // 7. GitHub Dark
  {
    id: 'github-dark',
    name: 'GitHub Dark',
    mode: 'dark',
    colors: {
      background: '#0d1117',
      foreground: '#c9d1d9',
      cursor: '#c9d1d9',
      cursorAccent: '#0d1117',
      selectionBackground: 'rgba(56, 79, 120, 0.6)',
      selectionForeground: '#c9d1d9',
      black: '#484f58',
      red: '#ff7b72',
      green: '#3fb950',
      yellow: '#d29922',
      blue: '#58a6ff',
      magenta: '#bc8cff',
      cyan: '#39c5cf',
      white: '#b1bac4',
      brightBlack: '#6e7681',
      brightRed: '#ffa198',
      brightGreen: '#56d364',
      brightYellow: '#e3b341',
      brightBlue: '#79c0ff',
      brightMagenta: '#d2a8ff',
      brightCyan: '#56d4dd',
      brightWhite: '#f0f6fc',
    },
  },

  // 8. Catppuccin Mocha
  {
    id: 'catppuccin-mocha',
    name: 'Catppuccin Mocha',
    mode: 'dark',
    colors: {
      background: '#1e1e2e',
      foreground: '#cdd6f4',
      cursor: '#f5e0dc',
      cursorAccent: '#1e1e2e',
      selectionBackground: 'rgba(88, 91, 112, 0.4)',
      selectionForeground: '#cdd6f4',
      black: '#45475a',
      red: '#f38ba8',
      green: '#a6e3a1',
      yellow: '#f9e2af',
      blue: '#89b4fa',
      magenta: '#f5c2e7',
      cyan: '#94e2d5',
      white: '#bac2de',
      brightBlack: '#585b70',
      brightRed: '#f38ba8',
      brightGreen: '#a6e3a1',
      brightYellow: '#f9e2af',
      brightBlue: '#89b4fa',
      brightMagenta: '#f5c2e7',
      brightCyan: '#94e2d5',
      brightWhite: '#a6adc8',
    },
  },

  // 9. Gruvbox Dark
  {
    id: 'gruvbox-dark',
    name: 'Gruvbox Dark',
    mode: 'dark',
    colors: {
      background: '#282828',
      foreground: '#ebdbb2',
      cursor: '#ebdbb2',
      cursorAccent: '#282828',
      selectionBackground: 'rgba(80, 73, 69, 0.6)',
      selectionForeground: '#ebdbb2',
      black: '#282828',
      red: '#cc241d',
      green: '#98971a',
      yellow: '#d79921',
      blue: '#458588',
      magenta: '#b16286',
      cyan: '#689d6a',
      white: '#a89984',
      brightBlack: '#928374',
      brightRed: '#fb4934',
      brightGreen: '#b8bb26',
      brightYellow: '#fabd2f',
      brightBlue: '#83a598',
      brightMagenta: '#d3869b',
      brightCyan: '#8ec07c',
      brightWhite: '#ebdbb2',
    },
  },

  // 10. Tokyo Night
  {
    id: 'tokyo-night',
    name: 'Tokyo Night',
    mode: 'dark',
    colors: {
      background: '#1a1b26',
      foreground: '#a9b1d6',
      cursor: '#c0caf5',
      cursorAccent: '#1a1b26',
      selectionBackground: 'rgba(51, 70, 124, 0.5)',
      selectionForeground: '#a9b1d6',
      black: '#15161e',
      red: '#f7768e',
      green: '#9ece6a',
      yellow: '#e0af68',
      blue: '#7aa2f7',
      magenta: '#bb9af7',
      cyan: '#7dcfff',
      white: '#a9b1d6',
      brightBlack: '#414868',
      brightRed: '#f7768e',
      brightGreen: '#9ece6a',
      brightYellow: '#e0af68',
      brightBlue: '#7aa2f7',
      brightMagenta: '#bb9af7',
      brightCyan: '#7dcfff',
      brightWhite: '#c0caf5',
    },
  },

  // ═══════════════════════════════════════════
  // Light Terminal Themes
  // ═══════════════════════════════════════════

  // 11. DBGo Light
  {
    id: 'dbgo-light',
    name: 'DBGo Light',
    mode: 'light',
    colors: {
      background: '#ffffff',
      foreground: '#1a1a2e',
      cursor: '#2563eb',
      cursorAccent: '#ffffff',
      selectionBackground: 'rgba(37, 99, 235, 0.2)',
      selectionForeground: '#1a1a2e',
      black: '#e5e7eb',
      red: '#dc2626',
      green: '#16a34a',
      yellow: '#d97706',
      blue: '#2563eb',
      magenta: '#7c3aed',
      cyan: '#0891b2',
      white: '#1a1a2e',
      brightBlack: '#9ca3af',
      brightRed: '#ef4444',
      brightGreen: '#22c55e',
      brightYellow: '#eab308',
      brightBlue: '#3b82f6',
      brightMagenta: '#8b5cf6',
      brightCyan: '#06b6d4',
      brightWhite: '#0f172a',
    },
  },

  // 12. GitHub Light
  {
    id: 'github-light',
    name: 'GitHub Light',
    mode: 'light',
    colors: {
      background: '#ffffff',
      foreground: '#24292e',
      cursor: '#044289',
      cursorAccent: '#ffffff',
      selectionBackground: 'rgba(3, 102, 214, 0.15)',
      selectionForeground: '#24292e',
      black: '#d1d5da',
      red: '#cf222e',
      green: '#116329',
      yellow: '#4d2d00',
      blue: '#0550ae',
      magenta: '#8250df',
      cyan: '#1b7c83',
      white: '#24292e',
      brightBlack: '#6e7781',
      brightRed: '#a40e26',
      brightGreen: '#1a7f37',
      brightYellow: '#633c01',
      brightBlue: '#0969da',
      brightMagenta: '#6639ba',
      brightCyan: '#3192aa',
      brightWhite: '#0d1117',
    },
  },

  // 13. Solarized Light
  {
    id: 'solarized-light',
    name: 'Solarized Light',
    mode: 'light',
    colors: {
      background: '#fdf6e3',
      foreground: '#657b83',
      cursor: '#586e75',
      cursorAccent: '#fdf6e3',
      selectionBackground: 'rgba(238, 232, 213, 0.8)',
      selectionForeground: '#586e75',
      black: '#eee8d5',
      red: '#dc322f',
      green: '#859900',
      yellow: '#b58900',
      blue: '#268bd2',
      magenta: '#d33682',
      cyan: '#2aa198',
      white: '#073642',
      brightBlack: '#93a1a1',
      brightRed: '#cb4b16',
      brightGreen: '#586e75',
      brightYellow: '#657b83',
      brightBlue: '#839496',
      brightMagenta: '#6c71c4',
      brightCyan: '#93a1a1',
      brightWhite: '#002b36',
    },
  },

  // 14. One Light
  {
    id: 'one-light',
    name: 'One Light',
    mode: 'light',
    colors: {
      background: '#fafafa',
      foreground: '#383a42',
      cursor: '#526fff',
      cursorAccent: '#fafafa',
      selectionBackground: 'rgba(229, 229, 230, 0.7)',
      selectionForeground: '#383a42',
      black: '#e5e5e6',
      red: '#e45649',
      green: '#50a14f',
      yellow: '#c18401',
      blue: '#4078f2',
      magenta: '#a626a4',
      cyan: '#0184bc',
      white: '#383a42',
      brightBlack: '#a0a1a7',
      brightRed: '#ca1243',
      brightGreen: '#50a14f',
      brightYellow: '#986801',
      brightBlue: '#4078f2',
      brightMagenta: '#a626a4',
      brightCyan: '#0184bc',
      brightWhite: '#232324',
    },
  },

  // 15. Catppuccin Latte
  {
    id: 'catppuccin-latte',
    name: 'Catppuccin Latte',
    mode: 'light',
    colors: {
      background: '#eff1f5',
      foreground: '#4c4f69',
      cursor: '#dc8a78',
      cursorAccent: '#eff1f5',
      selectionBackground: 'rgba(172, 176, 190, 0.4)',
      selectionForeground: '#4c4f69',
      black: '#e6e9ef',
      red: '#d20f39',
      green: '#40a02b',
      yellow: '#df8e1d',
      blue: '#1e66f5',
      magenta: '#8839ef',
      cyan: '#04a5e5',
      white: '#4c4f69',
      brightBlack: '#9ca0b0',
      brightRed: '#d20f39',
      brightGreen: '#40a02b',
      brightYellow: '#df8e1d',
      brightBlue: '#1e66f5',
      brightMagenta: '#8839ef',
      brightCyan: '#04a5e5',
      brightWhite: '#232634',
    },
  },
];

// ═══════════════════════════════════════════
// Filtered theme lists by mode
// ═══════════════════════════════════════════

export const DARK_EDITOR_THEMES = EDITOR_THEMES.filter(t => t.mode === 'dark');
export const LIGHT_EDITOR_THEMES = EDITOR_THEMES.filter(t => t.mode === 'light');
export const DARK_TERMINAL_THEMES = TERMINAL_THEMES.filter(t => t.mode === 'dark');
export const LIGHT_TERMINAL_THEMES = TERMINAL_THEMES.filter(t => t.mode === 'light');
